import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import omit from 'lodash/omit';
import { useOpenAi } from '@/hooks/useOpenAi';
import { InterviewScore, InterviewScoreEnum, User } from '@/types/api';
import { capitalizeString } from '@/utils/global';
import {
  defaultRatingQuantity,
  getNormalizedScore,
  getWeightedCurrentScore,
  getWeightedScore,
  interviewsThatHasNotes,
  InterviewTypes,
} from '@/utils/interviews';
import InterviewReviewComponent, { Score } from './InterviewReview.component';
import { CustomReviewType } from '../InterviewReviewsManager/InterviewReviewsManager.component';

export type InterviewReviewProps = {
  inputList: InterviewScore[];
  index: number;
  id?: string;
  note?: string;
  reviewer?: User;
  isActionLoading: boolean;
  reviewedAt?: string;
  interviewType: InterviewTypes;
  onCancel: (interviewReviewIndex: number) => void;
  onSave: (index: number, newReview: CustomReviewType) => Promise<boolean>;
  onGenerateReviewWithAI?: (
    interviewReviewIndex: number,
    newReview: CustomReviewType
  ) => Promise<boolean>;
  onDelete: (interviewReviewId: string) => Promise<boolean>;
};

type EditingReview = {
  id: string;
  reviewer: User | undefined;
  reviewedAt: Date | undefined;
  note: string;
  prevNote: string;
  isEditing: boolean;
};

export default function InterviewReview({
  inputList,
  isActionLoading,
  index,
  id = '',
  note = '',
  reviewer,
  reviewedAt,
  interviewType,
  onSave,
  onGenerateReviewWithAI,
  onDelete,
  onCancel,
}: InterviewReviewProps): JSX.Element {
  const [scores, setScores] = useState<Score[]>([]);

  const [review, setReview] = useState<EditingReview>(() => {
    return {
      id,
      reviewer,
      reviewedAt: reviewedAt ? new Date(reviewedAt) : undefined,
      note,
      prevNote: note,
      isEditing: !id,
    };
  });

  useEffect(() => {
    setReview((prev) => ({
      ...prev,
      note,
      id,
      reviewedAt: reviewedAt ? new Date(reviewedAt) : undefined,
    }));
  }, [id, note, reviewedAt]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (inputList[0].score) {
      const formattedScores = inputList.map((inputScore) => ({
        type: inputScore.scoreType,
        name: `${inputScore.scoreType.toLowerCase()}-${id}`,
        label: capitalizeString(inputScore.scoreType),
        score: getNormalizedScore(id, inputScore.score, interviewType, inputScore.scoreType),
        prevScore: getNormalizedScore(id, inputScore.score, interviewType, inputScore.scoreType),
        id: inputScore.id as string,
      }));

      setScores(formattedScores);
    }
  }, [id, inputList, interviewType]);

  const onChangeScore = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value, name } = event.target;

    setScores((prevScores) => {
      const selectedScoreIndex = prevScores.findIndex((score) => score.name === name);

      const currentScores = prevScores;
      currentScores[selectedScoreIndex].prevScore = currentScores[selectedScoreIndex].score;
      currentScores[selectedScoreIndex].score = value;

      return [...currentScores];
    });
  };

  const onToggleIsEditing = (): void => {
    setReview((prev) => ({
      ...prev,
      isEditing: !prev.isEditing,
    }));
  };

  const onChangeNote = (value: string): void => {
    setReview((prev) => ({
      ...prev,
      note: value,
    }));
  };

  const onCancelEdition = (triggerCallback = true): void => {
    setReview((prev) => ({
      ...prev,
      note: prev.prevNote,
    }));

    setScores((prevScores) =>
      prevScores.map((inputScore) => ({ ...inputScore, score: inputScore.prevScore ?? '1' }))
    );

    if (triggerCallback && !reviewedAt) {
      onCancel(index);
    }

    onToggleIsEditing();
  };

  const getFormattedReview = (
    reviewToFormat: EditingReview,
    reviewScores: InterviewScore[]
  ): CustomReviewType => {
    return {
      ...omit(reviewToFormat, ['reviewer', 'reviewedAt', 'isEditing', 'prevNote']),
      user: reviewToFormat.reviewer,
      score: reviewScores,
    };
  };

  const onSaveReview = async (): Promise<void> => {
    const newReview = {
      ...review,
      prevNote: review.note,
      reviewedAt: new Date(),
      reviewer,
    };

    const score = scores.map((inputScore) => ({
      scoreType: InterviewScoreEnum[inputScore.type],
      score: getWeightedScore(
        Number(inputScore.score),
        interviewType,
        InterviewScoreEnum[inputScore.type]
      ),
      id: inputScore.id,
    }));

    const isSaved = await onSave(index, getFormattedReview(newReview, score));

    if (isSaved) {
      setReview((prev) => ({
        ...prev,
        ...newReview,
      }));
      onToggleIsEditing();
    } else {
      onCancelEdition(false);
    }
  };

  const onGenerateReview = async (): Promise<void> => {
    const newReview = {
      ...review,
      prevNote: review.note,
      reviewedAt: new Date(),
      reviewer,
    };

    const score = scores.map((inputScore) => ({
      scoreType: InterviewScoreEnum[inputScore.type],
      score: getWeightedScore(
        Number(inputScore.score),
        interviewType,
        InterviewScoreEnum[inputScore.type]
      ),
      id: inputScore.id,
    }));

    const isSaved = await onGenerateReviewWithAI?.(index, getFormattedReview(newReview, score));

    if (isSaved) {
      setReview((prev) => ({
        ...prev,
        ...newReview,
      }));
      onToggleIsEditing();
    } else {
      onCancelEdition(false);
    }
  };

  const onToggleDeleteModal = (event?: any): void => {
    event?.stopPropagation();
    setIsDeleteModalOpen((prev) => !prev);
  };

  const onDeleteReview = async (): Promise<void> => {
    onToggleDeleteModal();

    const isDeleted = await onDelete(review.id);

    if (isDeleted) {
      onToggleIsEditing();
    }
  };

  const ratingQuantity = defaultRatingQuantity[interviewType];

  const totalScore = useMemo(
    () =>
      interviewsThatHasNotes.includes(interviewType) ? 100 : inputList.length * ratingQuantity,
    [inputList.length, interviewType, ratingQuantity]
  );

  const currentScore = useMemo(
    () => getWeightedCurrentScore(interviewType, scores),
    [interviewType, scores]
  );

  const canSave = useMemo(() => {
    const hasMoreThanDefaultScore = currentScore > inputList.length;
    const noteIsNotEmpty = !!review.note;
    const isReviewed = !!review.reviewedAt;

    return hasMoreThanDefaultScore || noteIsNotEmpty || isReviewed;
  }, [currentScore, inputList.length, review.note, review.reviewedAt]);

  return (
    <InterviewReviewComponent
      reviewer={review.reviewer}
      reviewedAt={review.reviewedAt}
      isEditing={review.isEditing}
      canSave={canSave}
      note={review.note}
      scores={scores}
      totalScore={totalScore}
      currentScore={currentScore}
      ratingQuantity={ratingQuantity}
      isActionLoading={isActionLoading}
      onChangeScore={onChangeScore}
      onChangeNote={onChangeNote}
      onToggleIsEditing={onToggleIsEditing}
      onSaveReview={onSaveReview}
      onGenerateReviewWithAI={onGenerateReview}
      onDeleteReview={onDeleteReview}
      isDeleteModalOpen={isDeleteModalOpen}
      onToggleDeleteModal={onToggleDeleteModal}
      onCancelEdition={onCancelEdition}
    />
  );
}
